import { Booking } from "../types/booking";
import { generateAndSavePackingSlip } from "../utils/packingSlip";
import { supabase } from "../lib/supabase";
import { trackFeatureUsage } from "../lib/posthog";
import { message } from "antd";

// Define additional interfaces for better type checking
interface SupplierInfo {
  id?: number;
  name: string;
  email?: string;
  phone?: string;
  country?: string;
  province?: string;
  address?: string;
  postal_code?: string;
  [key: string]: unknown;
}

interface CompanyInfo {
  id?: number;
  name: string;
  address?: string;
  city?: string;
  country?: string;
  postal_code?: string;
  eori_number?: string;
  coc_number?: string;
  [key: string]: unknown;
}

interface TransportTypeInfo {
  name: string;
  abbreviation: string;
  [key: string]: unknown;
}

// Product info interface that matches what we need
interface ProductItem {
  id: number;
  name: string;
  pieces?: number | null;
  cartons?: number | null;
  gross_weight: number;
  cbm: number;
  ean: any; // Using any to avoid type conflicts
  hs_code?: string | null;
  [key: string]: unknown;
}

// Interface matching packingSlip.ts expected format
interface FormattedBooking {
  id: number;
  reference: string;
  transport_type: {
    name: string;
    abbreviation: string;
  };
  created_at: string;
  supplier: {
    name: string;
    email: string;
    phone: string;
    country: string;
    city: string;
    address: string;
    postal_code?: string;
  };
  company: {
    id: number;
    name: string;
    address: string;
    city: string;
    country: string;
    postal_code?: string;
    eori_number: string;
    coc_number: string;
    street_name: string;
    house_number: string;
  };
  products: {
    id: number;
    name: string;
    pieces?: number | null;
    cartons?: number | null;
    gross_weight: number;
    cbm: number;
    ean: number | null;
    hs_code?: string | null;
    net_weight: number;
  }[];
}

// Our own DeliveryInfo interface
interface DeliveryInfoType {
  street_name?: string;
  house_number?: string;
  [key: string]: unknown;
}

/**
 * Generate and download a packing slip for a booking
 */
export const generateAndDownloadPackingSlip = async (booking: Booking): Promise<boolean> => {
  try {
    // Always use direct PDF generation approach for consistency
    message.loading("Generating packing slip...", 0);
    
    // Get supplier info safely with proper typing
    const supplierInfo = Array.isArray(booking.supplier_info) 
      ? booking.supplier_info[0] 
      : booking.supplier_info || {};
    const supplier = supplierInfo as SupplierInfo;
      
    // Get company info safely with proper typing
    const companyInfo = Array.isArray(booking.company_info) 
      ? booking.company_info[0] 
      : booking.company_info || {};
    const company = companyInfo as CompanyInfo;
      
    // Get transport type safely with proper typing
    const transportTypeInfo = Array.isArray(booking.transport_type_info) 
      ? booking.transport_type_info[0] 
      : booking.transport_type_info || {};
    const transportType = transportTypeInfo as TransportTypeInfo;
      
    // Get products safely with proper typing
    let productsData = [];
    
    if (Array.isArray(booking.product_info)) {
      if (booking.product_info.length > 0) {
        if (Array.isArray(booking.product_info[0])) {
          productsData = booking.product_info[0];
        } else {
          productsData = booking.product_info;
        }
      }
    }
    
    // Use type assertion to work around type incompatibilities
    const productArray = productsData as ProductItem[];
    
    // Get delivery info safely with proper type casting
    let deliveryInfo: DeliveryInfoType = { street_name: "", house_number: "" };
    
    if (booking.booking_delivery_info) {
      if (Array.isArray(booking.booking_delivery_info) && booking.booking_delivery_info.length > 0) {
        // Cast to our own type to avoid type conflicts
        deliveryInfo = {
          street_name: (booking.booking_delivery_info[0] as any).street_name || "",
          house_number: (booking.booking_delivery_info[0] as any).house_number || "",
        };
      } else {
        // Cast to our own type to avoid type conflicts
        deliveryInfo = {
          street_name: (booking.booking_delivery_info as any).street_name || "",
          house_number: (booking.booking_delivery_info as any).house_number || "",
        };
      }
    }
    
    // Format booking data for the packing slip generator
    const formattedBooking: FormattedBooking = {
      id: booking.id,
      reference: booking.reference || `EFF${booking.id}`,
      transport_type: {
        name: transportType.name || "Unknown",
        abbreviation: transportType.abbreviation || ""
      },
      created_at: booking.created_at,
      supplier: {
        name: supplier.name || "Unknown",
        email: supplier.email || "",
        phone: supplier.phone || "",
        country: supplier.country || "",
        city: supplier.province || "", // Use province as city
        address: supplier.address || "",
        postal_code: supplier.postal_code
      },
      company: {
        id: company.id || 0,
        name: company.name || "Unknown",
        address: company.address || "",
        city: company.city || "",
        country: company.country || "",
        postal_code: company.postal_code,
        eori_number: company.eori_number || "",
        coc_number: company.coc_number || "",
        street_name: deliveryInfo.street_name || "",
        house_number: deliveryInfo.house_number || ""
      },
      products: productArray.map(p => ({
        id: p.id || 0,
        name: p.name || "Unknown",
        pieces: p.pieces,
        cartons: p.cartons,
        gross_weight: p.gross_weight || 0,
        cbm: p.cbm || 0, 
        ean: p.ean || null, // Convert to null for the expected type
        hs_code: p.hs_code || null,
        net_weight: p.gross_weight ? p.gross_weight * 0.9 : 0
      }))
    };
    
    // Generate and save the packing slip
    const filePath = await generateAndSavePackingSlip(formattedBooking);
    message.destroy();
    
    if (!filePath) {
      message.error("Failed to generate packing slip");
      return false;
    }
    
    // Create a signed URL to download the file
    const { data, error } = await supabase.storage
      .from("packingslips")
      .createSignedUrl(filePath, 60 * 60); // 1 hour expiry
    
    if (error || !data?.signedUrl) {
      message.error("Failed to generate download link");
      return false;
    }
    
    // Open the download link in a new tab
    window.open(data.signedUrl, "_blank");
    
    // Track usage
    trackFeatureUsage("admin_booking_packingslip_download");
    
    return true;
  } catch (error) {
    console.error("Error generating packing slip:", error);
    message.destroy();
    message.error("Failed to generate packing slip");
    return false;
  }
};

/**
 * Get a previously generated packing slip
 */
export const getPackingSlip = async (booking: Booking): Promise<string | null> => {
  try {
    // Get transport type safely
    const transportType = Array.isArray(booking.transport_type_info) 
      ? booking.transport_type_info[0] 
      : booking.transport_type_info;
    
    const filePath = `${booking.company_id}/${booking.id}/packing_slip_${booking.id}_${
      transportType?.abbreviation || ""
    }.pdf`;
    
    // Check if file exists
    const { data, error } = await supabase.storage
      .from("packingslips")
      .createSignedUrl(filePath, 60 * 60); // 1 hour expiry
    
    if (error || !data?.signedUrl) {
      return null;
    }
    
    return data.signedUrl;
  } catch (error) {
    console.error("Error getting packing slip:", error);
    return null;
  }
}; 